import React, { useState, useEffect, useCallback } from 'react';
import { vendorsApi } from '../../network/apis/vendors';
import Button from '../../components/elements/button';
import { Search, Filter, Eye, Clock } from '../../assets/icons';
import '../../assets/css/vendor/orders.css';

const VendorOrders = () => {
    const [loading, setLoading] = useState(true);
    const [orders, setOrders] = useState([]);
    const [statistics, setStatistics] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [updatingStatus, setUpdatingStatus] = useState(null);

    const fetchOrders = useCallback(async () => {
        try {
            setLoading(true);
            const params = {};
            if (statusFilter !== 'all') {
                params.status = statusFilter;
            }
            const response = await vendorsApi.getOrders(params);
            const data = response.data?.data || response.data || [];
            setOrders(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Failed to fetch orders:', err);
            setError('Failed to load orders. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [statusFilter]);

    const fetchStatistics = useCallback(async () => {
        try {
            const response = await vendorsApi.getOrderStatistics();
            setStatistics(response.data?.data || response.data);
        } catch (err) {
            console.error('Failed to fetch statistics:', err);
        }
    }, []);

    useEffect(() => {
        fetchOrders();
        fetchStatistics();
    }, [fetchOrders, fetchStatistics]);

    // Filter orders based on search
    const filteredOrders = orders.filter(order => {
        const matchesSearch = 
            order.order_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            order.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            order.customer_email?.toLowerCase().includes(searchTerm.toLowerCase());
        return matchesSearch;
    });

    const handleUpdateStatus = async (orderId, newStatus) => {
        try {
            setUpdatingStatus(orderId);
            await vendorsApi.updateOrderStatus(orderId, newStatus);
            setOrders(prev => prev.map(o => 
                o.id === orderId ? { ...o, status: newStatus } : o
            ));
            if (selectedOrder?.id === orderId) {
                setSelectedOrder(prev => ({ ...prev, status: newStatus }));
            }
            setSuccess('Order status updated successfully');
            setTimeout(() => setSuccess(''), 3000);
            fetchStatistics();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to update order status');
            setTimeout(() => setError(''), 3000);
        } finally {
            setUpdatingStatus(null);
        }
    };

    const getStatusClass = (status) => {
        switch (status) {
            case 'PENDING': return 'pending';
            case 'PROCESSING': return 'processing';
            case 'SHIPPED': return 'shipped';
            case 'DELIVERED': return 'delivered';
            case 'CANCELLED': return 'cancelled';
            default: return '';
        }
    };

    const getStatusLabel = (status) => {
        switch (status) {
            case 'PENDING': return 'Pending';
            case 'PROCESSING': return 'Processing';
            case 'SHIPPED': return 'Shipped';
            case 'DELIVERED': return 'Delivered';
            case 'CANCELLED': return 'Cancelled';
            default: return status;
        }
    };

    const getNextStatus = (currentStatus) => {
        switch (currentStatus) {
            case 'PENDING': return 'PROCESSING';
            case 'PROCESSING': return 'SHIPPED';
            case 'SHIPPED': return 'DELIVERED';
            default: return null;
        }
    };

    const getNextStatusLabel = (currentStatus) => {
        const next = getNextStatus(currentStatus);
        if (!next) return null;
        return `Mark as ${getStatusLabel(next)}`;
    };

    const formatPrice = (price) => {
        return new Intl.NumberFormat('en-NG', {
            style: 'currency',
            currency: 'NGN',
            minimumFractionDigits: 0
        }).format(price || 0);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-NG', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) {
        return (
            <div className="vendor-orders">
                <div className="loading-state">
                    <div className="spinner"></div>
                    <p>Loading orders...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="vendor-orders">
            {/* Alert Messages */}
            {error && (
                <div className="alert alert-error">
                    <span>{error}</span>
                    <button onClick={() => setError('')}>&times;</button>
                </div>
            )}
            {success && (
                <div className="alert alert-success">
                    <span>{success}</span>
                    <button onClick={() => setSuccess('')}>&times;</button>
                </div>
            )}

            {/* Page Header */}
            <div className="page-header">
                <div className="header-content">
                    <h1>Orders</h1>
                    <p className="subtitle">{filteredOrders.length} orders</p>
                </div>
            </div>

            {/* Statistics Cards */}
            {statistics && (
                <div className="stats-grid">
                    <div className="stat-card">
                        <div className="stat-icon pending">
                            <img src={Clock} alt="" />
                        </div>
                        <div className="stat-content">
                            <span className="stat-value">{statistics.pending_orders || 0}</span>
                            <span className="stat-label">Pending</span>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon processing">
                            <span>⚙️</span>
                        </div>
                        <div className="stat-content">
                            <span className="stat-value">{statistics.processing_orders || 0}</span>
                            <span className="stat-label">Processing</span>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon shipped">
                            <span>🚚</span>
                        </div>
                        <div className="stat-content">
                            <span className="stat-value">{statistics.shipped_orders || 0}</span>
                            <span className="stat-label">Shipped</span>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon delivered">
                            <span>✓</span>
                        </div>
                        <div className="stat-content">
                            <span className="stat-value">{statistics.delivered_orders || 0}</span>
                            <span className="stat-label">Delivered</span>
                        </div>
                    </div>
                    <div className="stat-card highlight">
                        <div className="stat-content">
                            <span className="stat-value">{formatPrice(statistics.total_revenue)}</span>
                            <span className="stat-label">Total Revenue</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Toolbar */}
            <div className="toolbar">
                <div className="search-container">
                    <img src={Search} alt="" className="search-icon" />
                    <input
                        type="text"
                        placeholder="Search by order number or customer..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                        <button className="clear-search" onClick={() => setSearchTerm('')}>&times;</button>
                    )}
                </div>
                
                <div className="filter-group">
                    <img src={Filter} alt="" className="filter-icon" />
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                    >
                        <option value="all">All Status</option>
                        <option value="PENDING">Pending</option>
                        <option value="PROCESSING">Processing</option>
                        <option value="SHIPPED">Shipped</option>
                        <option value="DELIVERED">Delivered</option>
                        <option value="CANCELLED">Cancelled</option>
                    </select>
                </div>
            </div>

            {/* Orders List */}
            {filteredOrders.length === 0 ? (
                <div className="empty-state">
                    <div className="empty-icon">📋</div>
                    <h3>No orders found</h3>
                    <p>
                        {orders.length === 0 
                            ? "You don't have any orders yet. Once customers purchase your products, orders will appear here."
                            : "No orders match your current filters."}
                    </p>
                </div>
            ) : (
                <div className="orders-list">
                    {filteredOrders.map(order => (
                        <div key={order.id} className="order-card">
                            <div className="order-header">
                                <div className="order-info">
                                    <h3 className="order-number">#{order.order_number}</h3>
                                    <span className="order-date">{formatDate(order.created_at)}</span>
                                </div>
                                <span className={`status-badge ${getStatusClass(order.status)}`}>
                                    {getStatusLabel(order.status)}
                                </span>
                            </div>

                            <div className="order-body">
                                <div className="customer-info">
                                    <div className="customer-avatar">
                                        {order.customer_name?.charAt(0)?.toUpperCase() || 'C'}
                                    </div>
                                    <div className="customer-details">
                                        <span className="customer-name">{order.customer_name || 'Customer'}</span>
                                        <span className="customer-email">{order.customer_email}</span>
                                    </div>
                                </div>

                                <div className="order-summary">
                                    <div className="summary-item">
                                        <span className="summary-label">Items</span>
                                        <span className="summary-value">{order.items?.length || 0}</span>
                                    </div>
                                    <div className="summary-item">
                                        <span className="summary-label">Your Total</span>
                                        <span className="summary-value price">{formatPrice(order.vendor_total)}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="order-footer">
                                <button 
                                    className="view-btn"
                                    onClick={() => setSelectedOrder(order)}
                                >
                                    <img src={Eye} alt="" />
                                    View Details
                                </button>
                                
                                {getNextStatus(order.status) && (
                                    <Button
                                        text={updatingStatus === order.id ? 'Updating...' : getNextStatusLabel(order.status)}
                                        variant="primary"
                                        onClick={() => handleUpdateStatus(order.id, getNextStatus(order.status))}
                                        disabled={updatingStatus === order.id}
                                    />
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Order Details Modal */}
            {selectedOrder && (
                <div className="modal-overlay" onClick={() => setSelectedOrder(null)}>
                    <div className="modal order-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <div>
                                <h3>Order #{selectedOrder.order_number}</h3>
                                <span className="order-date">{formatDate(selectedOrder.created_at)}</span>
                            </div>
                            <button 
                                className="modal-close"
                                onClick={() => setSelectedOrder(null)}
                            >
                                &times;
                            </button>
                        </div>
                        
                        <div className="modal-body">
                            {/* Status */}
                            <div className="detail-section">
                                <h4>Status</h4>
                                <div className="status-timeline">
                                    {['PENDING', 'PROCESSING', 'SHIPPED', 'DELIVERED'].map((status, index) => {
                                        const statusOrder = ['PENDING', 'PROCESSING', 'SHIPPED', 'DELIVERED'];
                                        const currentIndex = statusOrder.indexOf(selectedOrder.status);
                                        const isCompleted = index <= currentIndex;
                                        const isCurrent = status === selectedOrder.status;
                                        
                                        return (
                                            <div 
                                                key={status} 
                                                className={`timeline-step ${isCompleted ? 'completed' : ''} ${isCurrent ? 'current' : ''}`}
                                            >
                                                <div className="step-dot"></div>
                                                <span className="step-label">{getStatusLabel(status)}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Customer Info */}
                            <div className="detail-section">
                                <h4>Customer</h4>
                                <div className="detail-content">
                                    <p><strong>{selectedOrder.customer_name}</strong></p>
                                    <p>{selectedOrder.customer_email}</p>
                                    {selectedOrder.shipping_phone && (
                                        <p>{selectedOrder.shipping_phone}</p>
                                    )}
                                </div>
                            </div>

                            {/* Shipping Address */}
                            <div className="detail-section">
                                <h4>Shipping Address</h4>
                                <div className="detail-content">
                                    <p>{selectedOrder.shipping_address}</p>
                                    <p>{selectedOrder.shipping_city}, {selectedOrder.shipping_state}</p>
                                    {selectedOrder.shipping_postal_code && (
                                        <p>{selectedOrder.shipping_postal_code}</p>
                                    )}
                                </div>
                            </div>

                            {/* Order Items */}
                            <div className="detail-section">
                                <h4>Your Items</h4>
                                <div className="items-list">
                                    {selectedOrder.items?.map(item => (
                                        <div key={item.id} className="order-item">
                                            <div className="item-info">
                                                <span className="item-name">{item.product_name}</span>
                                                <span className="item-sku">SKU: {item.product_sku || 'N/A'}</span>
                                            </div>
                                            <div className="item-qty">× {item.quantity}</div>
                                            <div className="item-price">{formatPrice(item.total)}</div>
                                        </div>
                                    ))}
                                </div>
                                <div className="order-total">
                                    <span>Your Total</span>
                                    <span className="total-value">{formatPrice(selectedOrder.vendor_total)}</span>
                                </div>
                            </div>

                            {/* Notes */}
                            {selectedOrder.notes && (
                                <div className="detail-section">
                                    <h4>Notes</h4>
                                    <div className="detail-content">
                                        <p>{selectedOrder.notes}</p>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="modal-footer">
                            <Button 
                                text="Close" 
                                variant="outline"
                                onClick={() => setSelectedOrder(null)}
                            />
                            {getNextStatus(selectedOrder.status) && (
                                <Button
                                    text={updatingStatus === selectedOrder.id ? 'Updating...' : getNextStatusLabel(selectedOrder.status)}
                                    variant="primary"
                                    onClick={() => handleUpdateStatus(selectedOrder.id, getNextStatus(selectedOrder.status))}
                                    disabled={updatingStatus === selectedOrder.id}
                                />
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default VendorOrders;
